import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import {
  DeleteTicketingUserValidationSchema,
  TDeleteTicketingUserValidation,
  useDeleteTicketingUserMutation,
} from '../../api/delete-ticketing-user.api';
import { GET_LIST_TICKETING_USER_QUERY_KEY } from '../../api/get-list-ticketing-user.api';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';

import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

const TOAST_MESSAGES_DELETE_USER_TICKETING = {
  DELETE_USER_TICKETING_ERROR: 'Error delete user ticketing, please try again',
  DELETE_USER_TICKETING_SUCCESS: 'User ticketing deleted successfully!',
};

export type TDeleteUserTicketingModalType = 'DeleteUserTicketingModal';
export interface IDeleteUserTicketingModalProps {
  userId: string;
}
const DeleteUserTicketingModal = memo((props: IDeleteUserTicketingModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const form = useForm<TDeleteTicketingUserValidation>({
    resolver: zodResolver(DeleteTicketingUserValidationSchema),
    mode: 'onChange',
  });

  const mutationDeleteUserTicketing = useDeleteTicketingUserMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_USER_TICKETING.DELETE_USER_TICKETING_ERROR;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_TICKETING_USER_QUERY_KEY] });
        modalActions.closeModal('DeleteUserTicketingModal');
        showToast({
          message: TOAST_MESSAGES_DELETE_USER_TICKETING.DELETE_USER_TICKETING_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenDeleteUserTicketingModal = useMemo(() => {
    return modalActions.checkIsOpenModal('DeleteUserTicketingModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationDeleteUserTicketing.isPending;
  }, [mutationDeleteUserTicketing.isPending]);

  const isLoadingForm = useMemo(() => {
    return mutationDeleteUserTicketing.isPending;
  }, [mutationDeleteUserTicketing.isPending]);

  // EFFECTS
  useEffect(() => {
    if (props) {
      form.reset({
        userId: props?.userId || '',
      });
    }
  }, [props?.userId, form]);

  // HANDLERS
  const handleSubmitForm = useCallback(
    (data: TDeleteTicketingUserValidation) => {
      mutationDeleteUserTicketing.mutate({
        userId: data?.userId || '',
      });
    },
    [mutationDeleteUserTicketing]
  );

  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('DeleteUserTicketingModal');
  }, [modalActions]);

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  return (
    <Modal
      isOpen={isOpenDeleteUserTicketingModal}
      lockBodyScroll
      onClose={handleClose}
      className="p-[32px] md:w-[500px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Delete People</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitForm)(e);
          }}
          id="delete-user-ticketing-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this people?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="delete-user-ticketing-form" type="submit" disabled={isDisabledForm}>
          {isLoadingForm ? <Spinner className="w-[18px] h-[18px]" /> : null} Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
DeleteUserTicketingModal.displayName = 'DeleteUserTicketingModal';

export default DeleteUserTicketingModal;
